import { createContext, use } from 'react';

export enum FeatureToggle {
    logToggles = 'logToggles',
    isCorpusLinkEnabled = 'isCorpusLinkEnabled',
    absoluteSpanScore = 'absoluteSpanScore',
    bucketColors = 'bucketColors',
    isDatasetExplorerEnabled = 'isDatasetExplorerEnabled',
    isPeteishModelEnabled = 'isPeteishModelEnabled',
    isMultiModalEnabled = 'isMultiModalEnabled',
    isModelConfigEnabled = 'isModelConfigEnabled',
    isAgentPageEnabled = 'isAgentPageEnabled',
    isModelPageEnabled = 'isModelPageEnabled',
    isAnnouncementEnabled = 'isAnnouncementEnabled',
    isComparisonPageInternalOnly = 'isComparisonPageInternalOnly',
}

export type FeatureToggles = Record<FeatureToggle, boolean>;

export const defaultFeatureToggles: FeatureToggles = {
    [FeatureToggle.logToggles]: true,
    [FeatureToggle.isCorpusLinkEnabled]: false,
    [FeatureToggle.absoluteSpanScore]: true,
    [FeatureToggle.bucketColors]: true,
    [FeatureToggle.isDatasetExplorerEnabled]: false,
    [FeatureToggle.isPeteishModelEnabled]: false,
    [FeatureToggle.isMultiModalEnabled]: false,
    [FeatureToggle.isModelConfigEnabled]: false,
    [FeatureToggle.isAgentPageEnabled]: false,
    [FeatureToggle.isModelPageEnabled]: false,
    [FeatureToggle.isAnnouncementEnabled]: false,
    [FeatureToggle.isComparisonPageInternalOnly]: true,
};

const localStorageKey = 'feature-toggles';

type FTValue = string | boolean | number | undefined;

// allows easier use of toggles
const parseToggleValue = (toggleValue: FTValue): boolean => {
    switch (toggleValue?.toString().toLowerCase()) {
        case 'true':
        case '1':
        case 'on':
        case 'yes':
            return true;
        case 'false':
        case '0':
        case 'off':
        case 'no':
        case undefined:
        default:
            return false;
    }
};

const parseToggles = (toggles: Partial<Record<FeatureToggle, FTValue>>): FeatureToggles => {
    const ret: FeatureToggles = {} as FeatureToggles;
    Object.entries(toggles).forEach(([key, value]) => {
        if (key in FeatureToggle) {
            const kk: FeatureToggle = FeatureToggle[key as keyof typeof FeatureToggle];
            ret[kk] = parseToggleValue(value);
        }
    });
    return ret;
};

export const FeatureToggleContext = createContext(defaultFeatureToggles);

export function createToggles(initialToggles = defaultFeatureToggles) {
    // grab from local storage if we have any
    const localStorageToggles = parseToggles(
        JSON.parse(localStorage.getItem(localStorageKey) || '{}') as Record<string, FTValue>
    );

    // grab from url if we have any
    const query = new URL(window.location.href).searchParams;
    const queryToggles = parseToggles(Object.fromEntries(query));

    const envToggles = parseToggles({
        isCorpusLinkEnabled: process.env.VITE_IS_CORPUS_LINK_ENABLED,
        absoluteSpanScore: process.env.VITE_ABSOLUTE_SPAN_SCORE,
        bucketColors: process.env.VITE_BUCKET_COLORS,
        isDatasetExplorerEnabled: process.env.VITE_IS_DATASET_EXPLORER_ENABLED,
        isMultiModalEnabled: process.env.VITE_IS_MULTI_MODAL_ENABLED,
        isModelConfigEnabled: process.env.VITE_IS_MODEL_CONFIG_ENABLED,
        isAgentPageEnabled: process.env.VITE_IS_AGENT_PAGE_ENABLED,
        isModelPageEnabled: process.env.VITE_IS_MODEL_PAGE_ENABLED,
        isAnnouncementEnabled: process.env.VITE_IS_ANNOUNCEMENT_ENABLED,
        isComparisonPageInternalOnly: process.env.VITE_IS_COMPARISON_PAGE_INTERNAL_ONLY,
    });

    const toggles = {
        ...initialToggles,
        ...localStorageToggles,
        ...envToggles,
        ...queryToggles,
    };

    return toggles;
}

export function useFeatureToggles() {
    return use(FeatureToggleContext);
}

export function getFeatureToggles() {
    return createToggles();
}
